import { MateCardFactoryGenerateType } from "@/enums/chatEnums";
import { MateMailType } from "@/enums/mateEnums";
import {
  InteractionEventStatus,
  InteractionEventType,
  InvitationStatus,
  InvitationType,
} from "@/enums/mission";
import { deleteOne, fetchStream, get, post, put } from "@/modules/request";
import {
  ItemResponse,
  PageRequest,
  PageResponse,
  Response,
  WriteResponse,
} from "@/types/api-types";
import {
  InteractionEvent,
  InteractionPool,
  MateInvitation,
  MateMail,
  MateRelationship,
} from "@/types/entity";

// 互动池相关
export const ApiPostInteractionPool = (params: {
  mateId: number;
  spriteId?: number;
}): Promise<WriteResponse<InteractionPool>> => post("/InteractionPool", params);

export const ApiPutInteractionPool = (
  id: number,
  params: {
    spriteId: number;
  }
): Promise<WriteResponse<InteractionPool>> =>
  put("/InteractionPool/" + id, params);

export const ApiDeleteInteractionPool = (id: number) =>
  deleteOne("/InteractionPool/" + id);

export const ApiGetInteractionPoolPage = (
  params: PageRequest<null>
): Promise<{
  page: {
    additional: {
      estimatedPowerCubes: number;
    };
    current_page: number;
    data: InteractionPool[];
    path: string;
    per_page: number;
    total?: number;
  };
}> => get("/InteractionPoolPage", params);

export interface InteractionPoolSummary {
  pendingFriendRequestCount: number;
  pendingInteractionReviewCount: number;
  unreadMailCount: number;
  awaitingReceptionDispatchCount: number;
}

export const ApiGetInteractionPoolSummary = (): Promise<
  Response<InteractionPoolSummary>
> => get("/InteractionPoolSummary");

// 互动事件相关
export const ApiGetInteractionEventPage = (
  params: PageRequest<{
    mateId?: number;
    participantMateId?: number;
    type?: InteractionEventType | InteractionEventType[];
    isReviewed?: 0 | 1;
  }>
): Promise<PageResponse<InteractionEvent>> =>
  get("/InteractionEventPage", params);

export const ApiPutInteractionEvent = (
  id: number,
  params: {
    content: string;
  }
): Promise<WriteResponse<InteractionEvent>> =>
  put("/InteractionEvent/" + id, params);

export const ApiPostInteractionEventReview = (
  id: number,
  params: {
    reviewStatus:
      | InteractionEventStatus.Approved
      | InteractionEventStatus.Rejected;
  }
): Promise<WriteResponse<void>> =>
  post("/InteractionEventReview/" + id, params);

// 互动邮件相关
export const ApiPostMateMail = (
  params: Pick<
    MateMail,
    | "parentId"
    | "senderId"
    | "recipientId"
    | "subject"
    | "body"
    | "senderStatus"
    | "audioMediaId"
  >
): Promise<WriteResponse<MateMail>> => post("/MateMail", params);

export const ApiGetMateMail = (
  uuid: string
): Promise<WriteResponse<MateMail>> => get("/MateMail/" + uuid);

export const ApiPutMateMail = (
  id: number,
  params: Partial<
    Pick<
      MateMail,
      | "senderId"
      | "recipientId"
      | "subject"
      | "body"
      | "senderStatus"
      | "audioMediaId"
    >
  >
): Promise<WriteResponse<MateMail>> => put("/MateMail/" + id, params);

export const ApiDeleteMateMail = (id: number) => deleteOne("/MateMail/" + id);

export const ApiGetMateMailPage = (
  params: PageRequest<{
    rootId?: number;
    parentId?: number;
    mateId?: number;
    type: MateMailType;
    otherMateIds?: number[];
  }>
): Promise<PageResponse<MateMail>> => get("/MateMailPage", params);

export const ApiPostMateMailTrash = (id: number) =>
  post("/MateMailTrash/" + id);

export const ApiPostMateMailRead = (id: number) => post("/MateMailRead/" + id);

// 互动关系相关
export const ApiPostMateRelationship = (
  params: Pick<
    MateRelationship,
    "sourceMateId" | "targetMateId" | "name" | "description"
  >
): Promise<WriteResponse<MateRelationship>> =>
  post("/MateRelationship", params);

export const ApiDeleteMateRelationship = (id: number) =>
  deleteOne("/MateRelationship/" + id);

export const ApiPostMateRelationshipImportAdmire = (params: {
  mateId: number;
}): Promise<ItemResponse<MateRelationship>> =>
  post("/MateRelationship/ImportAdmirer", params);

export const ApiPutMateRelationship = (
  id: number,
  params: Pick<MateRelationship, "name" | "description" | "isBlocked">
): Promise<WriteResponse<MateRelationship>> =>
  put("/MateRelationship/" + id, params);

export const ApiGetMateRelationshipPage = (
  params: PageRequest<{
    targetType?: "mine" | "others";
    sourceMateId?: number;
    targetMateId?: number;
  }>
): Promise<PageResponse<MateRelationship>> =>
  get("/MateRelationshipPage", params);

export const ApiGetMateRelationship = (
  id: number
): Promise<WriteResponse<MateRelationship>> => get("/MateRelationship/" + id);

// 互动邀请相关
export const ApiPostMateInvitation = (params: {
  senderId: number;
  recipientId: number;
  type: InvitationType;
  message?: string;
}): Promise<WriteResponse<MateInvitation>> => post("/MateInvitation", params);

export const ApiGetMateInvitation = (
  uuid: string
): Promise<WriteResponse<MateInvitation>> => get("/MateInvitation/" + uuid);

export const ApiPostMateInvitationRespond = (
  id: number,
  params: {
    status: InvitationStatus;
  }
): Promise<WriteResponse<void>> => post("/MateInvitationRespond/" + id, params);

export const ApiPostMateInvitationCancel = (id: number) =>
  post("/MateInvitationCancel/" + id);

export function ApiPostMateMailCompleteSteam(
  params: {
    senderId: number;
    recipientId: number;
    subject: string;
    body: string;
    type: MateCardFactoryGenerateType;
    journalIdzz?: number[];
    mailIdzz?: number[];
    dispatchIdzz?: number[];
    memoryIdzz?: number[];
  },
  cb: (content: Response<{ text: string }> | string) => void
) {
  return fetchStream(`/MateMailComplete`, "POST", params, cb);
}
